import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';
import { surveysApi } from './api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';

/**
 * Read-only survey preview: title + meta + ordered list of steps with
 * components. Loads `GET /xy-xurvey/:id/preview` which returns the survey
 * along with its full step+component tree.
 */
export function SurveyPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const surveyId = Number(id);
  const navigate = useNavigate();
  const t = useT();

  const q = useQuery({
    queryKey: queryKeys.surveys.preview(surveyId),
    queryFn: () => surveysApi.preview(surveyId),
    enabled: Number.isFinite(surveyId),
  });

  if (q.isLoading) {
    return (
      <div className="page space-y-4">
        <Skeleton className="h-8 w-64 rounded" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }
  if (q.error || !q.data) {
    return (
      <p className="page text-sm text-[var(--color-fg-muted)]">{t('surveys.preview.notFound')}</p>
    );
  }

  const survey = q.data;
  const steps = survey.steps ?? [];

  return (
    <div className="page space-y-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>

      <header>
        <div className="mb-2 flex items-center gap-2">
          <Badge tone="accent">{t('surveys.preview.badge')}</Badge>
          {survey.productType && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-fg-muted)]">
              {survey.productType}
            </span>
          )}
          {survey.isLocked && <Badge tone="purple">{t('surveys.locked')}</Badge>}
          {survey.isTemplate && <Badge tone="info">{t('surveys.template')}</Badge>}
          {/* Caso 08 v1 — entry point to the in-app editor. Hidden for
              locked surveys; the BE refuses tree writes there anyway. */}
          {!survey.isLocked && (
            <Link
              to={`/surveys/builder/${surveyId}`}
              className="ml-auto inline-flex items-center gap-1 rounded-md border border-[var(--color-border-default)] px-3 py-1 text-xs font-semibold hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
            >
              <Pencil className="h-3 w-3" />
              {t('surveys.preview.editInBuilder')}
            </Link>
          )}
        </div>
        <h1 className="text-3xl font-bold">{survey.name}</h1>
        {survey.description && (
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{survey.description}</p>
        )}
        <p className="mt-2 text-xs text-[var(--color-fg-subtle)]">
          {t('surveys.preview.stepsAndComponents', {
            steps: steps.length,
            components: steps.reduce((n, s) => n + (s.components?.length ?? 0), 0),
          })}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t('surveys.preview.stepsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {steps.length === 0 && (
            <p className="text-sm text-[var(--color-fg-muted)]">{t('surveys.preview.noSteps')}</p>
          )}
          {steps.map((step, i) => (
            <div
              key={step.id}
              className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-4"
            >
              <p className="text-[10px] uppercase tracking-wider text-[var(--color-fg-subtle)]">
                {t('surveys.preview.stepN', { n: i + 1 })}
              </p>
              <p className="font-semibold">{step.name}</p>
              {step.description && (
                <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{step.description}</p>
              )}
              {step.components && step.components.length > 0 && (
                <ul className="mt-3 space-y-1.5 border-t border-[var(--color-border-subtle)] pt-3">
                  {step.components.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <span className="text-[var(--color-fg-primary)]">
                        {c.label || (
                          <span className="italic text-[var(--color-fg-subtle)]">
                            {t('surveys.preview.noLabel')}
                          </span>
                        )}
                      </span>
                      <Badge tone="neutral">{c.type}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

    </div>
  );
}
